import React, { createContext, useContext, useCallback } from 'react';
import {
  MsalProvider,
  useMsal,
  useIsAuthenticated,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  AccountInfo,
} from '@azure/msal-browser';
import { msalConfig, loginScopes, graphScopes, dataverseScopes } from './msalConfig';

// MSAL instance — created once
export const msalInstance = new PublicClientApplication(msalConfig);

// Auth context for convenience hooks
interface AuthContextValue {
  account: AccountInfo | null;
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getGraphToken: () => Promise<string>;
  getDataverseToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// Inner component that uses MSAL hooks (must be inside MsalProvider)
function AuthContextProvider({ children }: { children: React.ReactNode }) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = accounts[0] || null;

  const login = useCallback(async () => {
    await instance.loginRedirect({
      scopes: ['https://saragossa.crm11.dynamics.com/user_impersonation'],
    });
  }, [instance]);

  const logout = useCallback(async () => {
    await instance.logoutRedirect();
  }, [instance]);

  const acquireToken = useCallback(
    async (scopes: string[]): Promise<string> => {
      if (!account) throw new Error('No authenticated account');
      try {
        const response = await instance.acquireTokenSilent({ scopes, account });
        return response.accessToken;
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenRedirect({ scopes });
          return ''; // won't reach here — redirect happens
        }
        throw err;
      }
    },
    [instance, account]
  );

  const getGraphToken = useCallback(
    () => acquireToken(graphScopes),
    [acquireToken]
  );

  const getDataverseToken = useCallback(
    () => acquireToken(dataverseScopes),
    [acquireToken]
  );

  return (
    <AuthContext.Provider
      value={{ account, isAuthenticated, login, logout, getGraphToken, getDataverseToken }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Login screen shown when not authenticated
function LoginScreen() {
  const { instance } = useMsal();

  const handleLogin = () => {
    // Request Dataverse scope at login — this ensures consent is granted upfront.
    // Graph User.Read was admin-consented so it works silently after.
    instance.loginRedirect({
      scopes: ['https://saragossa.crm11.dynamics.com/user_impersonation'],
    });
  };

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-page)',
        gap: '24px',
      }}
    >
      <img src="/logo-white.svg" alt="Saragossa" style={{ height: '28px', opacity: 0.9 }} />
      <h1 style={{ fontSize: '16px', fontWeight: 500, color: 'var(--text-secondary)' }}>
        AI Readiness
      </h1>
      <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
        Sign in with your Saragossa account
      </p>
      <button className="btn btn-primary" onClick={handleLogin}>
        Sign in
      </button>
    </div>
  );
}

// Main provider — wraps the whole app
export function AuthProvider({ children }: { children: React.ReactNode }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthContextProvider>
        <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <LoginScreen />
        </UnauthenticatedTemplate>
      </AuthContextProvider>
    </MsalProvider>
  );
}
